import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'

/**
 * PullRequest document interface (read-only on the client). Populated server-side by the
 * Super Agent relay + GitHub webhook; a task can have multiple PRs over its life.
 */
export interface PullRequestDocument extends BaseDocument {
  taskId: string
  provider: string
  repositoryFullName: string
  number: number
  providerPullRequestId: string | null
  url: string | null
  htmlUrl: string | null
  sourceBranch: string | null
  targetBranch: string | null
  headRepoFullName: string | null
  headSha: string | null
  state: string
  ciStatus: string
  reviewStatus: string
  mergeability: string
  mergedOn: number | null
  providerUpdatedAt: number
  lastObservedSource: string | null
  authorGitHubLogin: string | null
}

const hooks = createEntityHooks<PullRequestDocument>('workitempullrequests')

/**
 * Subscribe to the pull requests linked to a task. Read-only — there are no client mutations
 * for this collection (server is the only writer). Pass `undefined` to disable while the task
 * id isn't available yet.
 */
export const usePullRequests = (taskId: string | undefined) => {
  const { data, isLoading, db } = hooks.useEntities(
    taskId ? { taskId } : undefined,
    { enabled: !!taskId },
  )
  return { pullRequests: data, isLoading, db }
}

/**
 * All non-deleted pull requests in the workspace. Used to compute at-a-glance card badges
 * across a board/list without one RxDB subscription per card.
 */
export const useAllPullRequests = () => {
  const { data, isLoading, db } = hooks.useEntities(undefined)
  return { pullRequests: data, isLoading, db }
}

/** Relevance rank for picking the PR a card should surface (lower = more relevant). */
export const PR_STATE_RANK: Record<string, number> = { open: 0, draft: 1, merged: 2, closed: 3 }

/** Pick the most relevant PR from a task's set: open > draft > merged > closed, then newest. */
export const mostRelevantPr = <T extends { state: string; providerUpdatedAt: number }>(
  prs: ReadonlyArray<T>,
): T | null =>
  prs.length === 0
    ? null
    : ([...prs].sort(
        (a, b) =>
          (PR_STATE_RANK[a.state] ?? 9) - (PR_STATE_RANK[b.state] ?? 9) ||
          b.providerUpdatedAt - a.providerUpdatedAt,
      )[0] ?? null)
